import { useState, createContext, useContext, type ReactNode } from 'react';
import { Button } from '@eye-able/ui';
import { InlineTranslationDialog } from './InlineTranslationDialog';
import { BookOpenCheck, EditIcon } from 'lucide-react';
import type { TOptions } from 'i18next';

type DialogProps = {
  key?: string;
  options?: TOptions;
};

type InlineTranslationContextType = {
  openDialog: (data: DialogProps) => void;
  editMode: boolean;
};
const InlineTranslationContext = createContext<InlineTranslationContextType>({
  openDialog: () => {},
  editMode: false,
});

export const useInlineTranslationDialog = () =>
  useContext(InlineTranslationContext);

export function InlineTranslationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [editMode, setEditMode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTranslation, setActiveTranslation] = useState<DialogProps>();

  const openDialog = (params: DialogProps) => {
    setIsDialogOpen(true);
    setActiveTranslation(params);
  };
  const closeDialog = () => {
    setActiveTranslation(undefined);
    setIsDialogOpen(false);
  };

  if (import.meta.env.VITE_INLINE_TRANSLATION !== 'true') {
    return children;
  }

  return (
    <InlineTranslationContext.Provider value={{ editMode, openDialog }}>
      {children}
      {isDialogOpen && (
        <InlineTranslationDialog
          translationKey={activeTranslation?.key}
          namespace={activeTranslation?.options?.ns?.toString()}
          onClose={() => closeDialog()}
        />
      )}
      <div className='eye:fixed eye:bottom-20 eye:right-20 eye:flex eye:gap-xs eye:z-1000'>
        <Button variant={'yellow'} onClick={() => setIsDialogOpen(true)}>
          Show translations dialog
        </Button>
        <Button variant={'yellow'} onClick={() => setEditMode((prev) => !prev)}>
          <EditIcon aria-hidden />
          {editMode ? 'Hide inline translations' : 'Show inline translations'}
        </Button>
        <Button variant={'black'} asChild>
          <a href='/i18n'>
            <BookOpenCheck aria-hidden /> Release
          </a>
        </Button>
      </div>
    </InlineTranslationContext.Provider>
  );
}
